import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid,
  PieChart, Pie, Cell, LineChart, Line,
} from "recharts";
import { Weight, Layers, Scale, Users } from "lucide-react";
import Layout from "@/components/Layout";
import { Panel, StatCard, LoadingCards, LoadingPanel, EmptyState, MachineBadge } from "@/components/widgets";
import { useFilter } from "@/context/FilterContext";
import client from "@/lib/api";
import { fmtKg, fmtInt, fmtPct, fmtTonnes, fmtDate, monthLabel, CHART_COLORS } from "@/lib/format";

export default function Executive() {
  const { query, filter, update, reset } = useFilter();
  const navigate = useNavigate();

  const { data, isLoading } = useQuery({
    queryKey: ["exec", query],
    queryFn: () => client.get(`/analytics/executive?${query}`).then((r) => r.data.data),
  });

  const k = data?.kpis;
  const empty = data && k?.recordCount === 0;

  const drillMachine = (code) => {
    update({ machineIds: [], search: code === "UNALLOCATED" ? "unalloc" : code });
    navigate("/records");
  };

  return (
    <Layout title="Executive Dashboard" subtitle={`${filter.from} → ${filter.to} · Africa/Johannesburg`}
      meta={k ? `${fmtInt(k.recordCount)} records` : null}>
      {isLoading ? (
        <div className="space-y-4"><LoadingCards /><LoadingPanel /></div>
      ) : empty ? (
        <Panel><EmptyState onReset={reset} /></Panel>
      ) : (
        <div className="space-y-5 animate-fade-up">
          {/* KPIs */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard label="Processed Weight" value={fmtTonnes(k.processedWeightKg)} unit="t" accent="#7C3AED"
              icon={Weight} sub={`${fmtKg(k.processedWeightKg)} kg total`} testid="kpi-processed-weight" />
            <StatCard label="Nesting Records" value={fmtInt(k.recordCount)} accent="#0D9488"
              icon={Layers} sub="Active + corrected" testid="kpi-record-count" />
            <StatCard label="Average Weight" value={fmtKg(k.averageWeightKg)} unit="kg" accent="#D97706"
              icon={Scale} sub="Per nesting record" testid="kpi-avg-weight" />
            <StatCard label="Active Customers" value={fmtInt(k.activeCustomerCount)} accent="#2563EB"
              icon={Users} sub={`${fmtPct(k.machineAllocationPercent)} machine allocated`} testid="kpi-active-customers" />
          </div>

          {/* Monthly throughput + Grade concentration */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
            <Panel title={data.throughputGranularity === "day" ? "Daily Processed Weight" : "Monthly Processed Weight"} className="lg:col-span-8" testid="panel-monthly">
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={data.monthlyThroughput}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" vertical={false} />
                  <XAxis dataKey="period"
                    tickFormatter={(v) => (data.throughputGranularity === "day" ? v.slice(8, 10) : monthLabel(v))}
                    tick={{ fontSize: 12, fill: "#64748B" }} axisLine={false} tickLine={false}
                    interval={data.throughputGranularity === "day" ? 1 : 0} minTickGap={6} />
                  <YAxis tickFormatter={(v) => `${(v / 1000).toFixed(0)}t`} tick={{ fontSize: 12, fill: "#64748B" }} axisLine={false} tickLine={false} />
                  <Tooltip formatter={(v) => [`${fmtKg(v)} kg`, "Weight"]}
                    labelFormatter={(v) => (data.throughputGranularity === "day" ? fmtDate(v) : monthLabel(v))}
                    contentStyle={{ borderRadius: 8, border: "1px solid #E2E8F0", fontSize: 13 }} />
                  <Bar dataKey="weightKg" fill="#7C3AED" radius={[5, 5, 0, 0]} maxBarSize={data.throughputGranularity === "day" ? 20 : 48} />
                </BarChart>
              </ResponsiveContainer>
            </Panel>

            <Panel title="Grade Concentration" className="lg:col-span-4" testid="panel-grade">
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie data={data.gradeConcentration} dataKey="weightKg" nameKey="displayName"
                    innerRadius={48} outerRadius={80} paddingAngle={2}>
                    {data.gradeConcentration.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                  </Pie>
                  <Tooltip formatter={(v) => `${fmtKg(v)} kg`} contentStyle={{ borderRadius: 8, fontSize: 13 }} />
                </PieChart>
              </ResponsiveContainer>
              <div className="mt-2 space-y-1.5 max-h-28 overflow-y-auto">
                {data.gradeConcentration.slice(0, 5).map((g, i) => (
                  <div key={g.id} className="flex items-center justify-between text-xs">
                    <span className="flex items-center gap-1.5 truncate">
                      <span className="w-2.5 h-2.5 rounded-sm" style={{ background: CHART_COLORS[i % CHART_COLORS.length] }} />
                      {g.displayName}
                    </span>
                    <span className="font-mono text-slate-500">{fmtPct(g.sharePercent)}</span>
                  </div>
                ))}
              </div>
            </Panel>
          </div>

          {/* Machine load + Top customers */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            <Panel title="Machine Load Distribution" testid="panel-machine-load">
              <div className="space-y-3">
                {data.machineLoad.map((m) => (
                  <button key={m.machineCode} onClick={() => drillMachine(m.machineCode)}
                    className="w-full text-left group" data-testid={`machine-load-${m.machineCode}`}>
                    <div className="flex items-center justify-between text-sm mb-1">
                      <span className="flex items-center gap-2"><MachineBadge code={m.machineCode} />
                        <span className="text-slate-500 text-xs">{fmtPct(m.sharePercent)}</span></span>
                      <span className="font-mono text-slate-700">{fmtKg(m.weightKg)} kg</span>
                    </div>
                    <div className="h-2.5 rounded-full bg-slate-100 overflow-hidden">
                      <div className="h-full rounded-full transition-all group-hover:opacity-80"
                        style={{ width: `${m.sharePercent}%`, background: m.machineCode === "UNALLOCATED" ? "#94A3B8" : "#7C3AED" }} />
                    </div>
                  </button>
                ))}
              </div>
            </Panel>

            <Panel title="Top Customers by Weight" testid="panel-top-customers">
              <div className="space-y-2">
                {data.topCustomers.map((c, i) => {
                  const max = data.topCustomers[0]?.weightKg || 1;
                  return (
                    <div key={c.id} className="flex items-center gap-3" data-testid={`top-customer-${i}`}>
                      <span className="w-5 text-xs font-mono text-slate-400">{i + 1}</span>
                      <div className="flex-1 min-w-0">
                        <div className="flex justify-between text-sm mb-0.5">
                          <span className="truncate text-slate-700">{c.displayName}</span>
                          <span className="font-mono text-xs text-slate-500">{fmtKg(c.weightKg)}</span>
                        </div>
                        <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden">
                          <div className="h-full rounded-full" style={{ width: `${(c.weightKg / max) * 100}%`, background: "#0D9488" }} />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </Panel>
          </div>
        </div>
      )}
    </Layout>
  );
}
